import { useEffect, useRef, useState } from 'react';

/**
 * A list shown newest first, with "Show earlier" pages under its first page. When the first page
 * loads again by itself (something changed), the earlier pages someone opened stay open: anything
 * pushed off the first page by newer items is kept with them, so nothing goes missing in between
 * (and anything gone from the first page for another reason, deleted, stays gone). A new subject
 * (`key`: the company, the person) starts from the first page again.
 */
export function useEarlier<T extends { id: string }>(first: readonly T[] | undefined, firstMore: boolean | undefined, key: string, at: (item: T) => string) {
  const [older, setOlder] = useState<T[]>([]);
  const [more, setMore] = useState(false);
  const shownFirst = useRef<readonly T[]>([]);
  const lastKey = useRef(key);
  const olderRef = useRef<T[]>([]);
  olderRef.current = older;
  const atRef = useRef(at);
  atRef.current = at;

  useEffect(() => {
    const now = first ?? [];
    const opened = olderRef.current.length > 0;
    const sameSubject = lastKey.current === key;
    lastKey.current = key;
    const before = shownFirst.current;
    shownFirst.current = now;
    // A new subject, nothing earlier opened, or the first page is everything there is.
    if (!sameSubject || !opened || !firstMore) {
      if (olderRef.current.length) setOlder([]);
      setMore(Boolean(firstMore));
      return;
    }
    const onFirst = new Set(now.map((x) => x.id));
    const oldest = now.length ? atRef.current(now[now.length - 1]!) : '';
    const seen = new Set<string>();
    const kept = [...before, ...olderRef.current].filter((x) => {
      if (onFirst.has(x.id) || seen.has(x.id)) return false;
      // Pushed down by newer ones (no newer than the first page's oldest), not gone for good.
      if (oldest && atRef.current(x) > oldest) return false;
      seen.add(x.id);
      return true;
    });
    setOlder(kept);
  }, [first, firstMore, key]);

  const list = [...(first ?? []), ...older];
  return {
    list,
    more,
    /** The next page, from "Show earlier". */
    addEarlier(rows: readonly T[], hasMore: boolean) {
      setOlder((o) => {
        const have = new Set([...(first ?? []), ...o].map((x) => x.id));
        return [...o, ...rows.filter((r) => !have.has(r.id))];
      });
      setMore(hasMore);
    },
    /** One that was deleted here. */
    drop(id: string) {
      setOlder((o) => o.filter((x) => x.id !== id));
    },
    /** One that changed here (undone, applied): its fresh copy, if it's on an earlier page. */
    replace(item: T) {
      setOlder((o) => (o.some((x) => x.id === item.id) ? o.map((x) => (x.id === item.id ? item : x)) : o));
    }
  };
}
